import * as React from "react";
import Spinner from "@amzn/awsui-components-react-v3/polaris/spinner";
import Alert from "@amzn/awsui-components-react-v3/polaris/alert";
import Popover from "@amzn/awsui-components-react-v3/polaris/popover";
import './CustomReactComponents.css';
import Flashbar from "@amzn/awsui-components-react-v3/polaris/flashbar";
import {ACCESS_DENIED_MESSAGE, DOWNLOAD_FILE_STATUS_MESSAGES, SHIPMENT_REQUEST_ACCESS_TICKET_LINK} from "./ConfigConstants";
import {EquipmentDataDropDownResult} from "../pages/index";
import { ZonedDateTime } from "../pages/components/ZonedDateTime";
import { ZonedDateTimeRange } from "../pages/components/ZonedDateTimeRange";
import { getTimezone } from './EquipmentUtil';

import { useQuery } from '@apollo/react-hooks';
import {
    Box,
    Button,
    Icon,
    Link,
    SpaceBetween,
    StatusIndicator,
    TextContent
} from "@amzn/awsui-components-react-v3";
import { AppConfig, AppConfigMapKey } from "../pages/config/AppConfig";
import { credentialStorage } from "@awsscm/argo-core/argo/utils/CredentialStorage";
import { Measurement } from "../pages/components/MeasurementComponent";

/**
 * This method would be used to display key-value pair on the UI.
 * @param label
 * @param value
 */
export function KeyValueStructure(label: string, value:any) {
  const valueContent = value || "-";
  return (<Box>
    <SpaceBetween size="l">
      <Box>
        <Box variant="awsui-key-label">
          {label}
        </Box>
        <Box>
          {valueContent}
        </Box>
      </Box>
    </SpaceBetween>
  </Box>);
}

/**
 * Build ReactNode for displaying list of strings along with info links.
 * @param arrayContent
 */
export function ListStructureWithInfoLink(arrayContent: string[]) {
    const argoPortalUrl = AppConfig.getConfigValue(AppConfigMapKey.ARGO_PORTAL_URL);
    const suffix = "/shipments/details/"
    const listItems = 
        arrayContent
            .map((value) => <Link external href={argoPortalUrl + suffix + value} variant="primary">{ value }</Link>);

    return (<TextContent>{listItems.length > 0 ? listItems : '-'}</TextContent>);
}

/**
 * Build ReactNode for displaying ticket links
 * @param ticketId
 */
export function TicketLink(ticketIds: string[]) {
    const length = ticketIds?.length;
    if (!length || length === 0) {
        return null;
    }

    return ticketIds.map(
        (id, index) => {
            const simUrl = AppConfig.getConfigValue(AppConfigMapKey.TICKET_URL) + "/" + id;
            const ticketCount = index + 1;
            return (
                <span key={index}> 
                    <Link external href={simUrl} variant="primary">{ "SIM " + ticketCount}</Link> {index < ticketIds.length - 1 && ", "} 
                </span>
            );
        }
    )
}

/**
 * Build ReactNode for displaying list of strings as unordered list
 * without bullet points using `list-without-bullets` class.
 * @param arrayContent
 */
function UnorderedNonBulletedListStructure(arrayContent: string[]) {
    const listItems =
        arrayContent
            .map((value) =>
                <li>{value}</li>);
    return (<TextContent><ul className="list-without-bullets">{listItems}</ul></TextContent>);
}

export function OrderedListStructure(arrayContent: string[]) {
    const length = arrayContent?.length;
    if (!length || length === 0) {
        return null;
    } else if (!Array.isArray(arrayContent)) {
        return arrayContent;
    }
    const listItems =
        arrayContent
            .map((value) =>
                <li>{value}</li>);
    return (<ol>{listItems}</ol>);
}

/**
 * This method is used to display List of strings on UI in the following format:
 * i.e. String1, String2 .. + 5 more
 * In this structure, we are just building the popover the 'N' more text and aren't adding content to the popover
 * as the React elements of this method aren't yet created till we return it to caller. So, we can't programmatically add the list of array into the Popover here.
 * @param arrayContent
 */
export function ListStructure(arrayContent: undefined | string[]) {
    const length = arrayContent?.length;
    if (!length || length === 0) {
        return null;
    }
    else if (length < 3) {
        return <Box>
            {arrayContent.reduce(
                (accumulator, currentValue, currentIndex, array) =>
                    accumulator.concat(", ", currentValue))}
        </Box>
    }
    return (<Box>
        {arrayContent[0]}, {arrayContent[1]}.. <Popover content={UnorderedNonBulletedListStructure(arrayContent)} dismissButton={false}>
        <Box> + {length-2} more</Box>
    </Popover>
    </Box>);
}

/** 
 * This method is used to display List of strings on UI the Copy to Clip Board Functionality
*/
export function ListStructureWithCopy(arrayContent: string[], listEntity: string) {
    const length = arrayContent?.length;
    if (!length || length === 0) {
        return null;
    }
    else if (length < 3) {
        return <Box>
            {arrayContent.reduce(
                (accumulator, currentValue, currentIndex, array) =>
                    accumulator.concat(", ", currentValue))}
        </Box>
    }
    return (<Box>
        {arrayContent[0]}, {arrayContent[1]}.. <SpaceBetween direction="horizontal" size="xxs">
            <Popover content={UnorderedNonBulletedListStructure(arrayContent)} dismissButton={false}>
                <Box> + {length-2} more</Box>
            </Popover>
            <Popover
                size="small"
                position="top"
                triggerType="custom"
                dismissButton={false}
                content={<StatusIndicator type="success">{listEntity} copied</StatusIndicator>}
            >
                <Button
                variant="inline-icon"
                iconName="copy"
                onClick={() => {
                    /* copy to clipboard implementation */
                    window.navigator.clipboard.writeText(arrayContent.join("\n"))
                }}
                />
            </Popover>
        </SpaceBetween>
    </Box>);
}

/**
 * Get the description popover with shortened text if string length is more than displayLength.
 * @param description
 * @param displayLength
 * @returns {JSX.Element|*} Popover Description element or null or undefined
 */
export function ShortenedStringWithPopover(description : any, displayLength = 23) {
    if (description === null || description === undefined || typeof description !== 'string' ) {
        return description;
    }
    else {
        let shortenedDescription = description;
        if (shortenedDescription.length <= displayLength) {
            return description;
        }
        shortenedDescription = shortenedDescription.substring(0, displayLength - 3).concat('...');
        return (
            <Popover
                size="large"
                position="top"
                triggerType="text"
                dismissButton={false}
                content={description}
            >
                {shortenedDescription}
            </Popover>
        );
    }
}

/**
 * This structure would be commonly used across our App to display a Loading message and a Spinner.
 * @param text
 */
export function Loader(text:string) {
  return (
      <Box className="loader-container">
        <Box variant="span" className="loader-container"><Spinner size="large"/><Box variant="p">Loading {text}</Box></Box>
      </Box>);
}

/**
 * This structure would be commonly used across our App to display a Loading message and a Spinner - inline, within a modal.
 * @param text
 */
export function InlineLoader(text: string) {
  return (
    <Box>
      <SpaceBetween size={'s'} direction={'horizontal'}>
        <Spinner size="normal"/>
        <Box display={'inline'} variant="p">{text}</Box>
      </SpaceBetween>
    </Box>
  );
}

/**
 * This structure would be commonly used across our App to display an Info message.
 * @param headerValue
 * @param text
 */
export function Info(headerValue:string, text:any) {
  return (
      <Alert header={headerValue} type="info">
        {text}
      </Alert>);
}

/**
 * This structure would be commonly used across our App to display an Error message.
 * @param headerValue
 * @param text
 * @param isDissmisible
 * @param onDismiss
 */
export let Error: (headerValue:string, text:any, isDissmisible?: boolean, isVisible?: boolean, onDismiss?: () => void) => any;
Error = function(headerValue:string, text:any, isDissmisible: boolean = false, isVisible: boolean = true, onDismiss) {
    return (
        <Alert header={headerValue} type="error" dismissible={isDissmisible} onDismiss={onDismiss} visible={isVisible}>
            {text}
        </Alert>);
};

/**
 * This structure would be commonly used across our App to display an Success message.
 * @param headerValue
 * @param text
 */
export function Success(headerValue:string, text:any) {
    return (
        <Alert header={headerValue} type="success" dismissible>
            {text}
        </Alert>);
}

export function DownloadInProgress() {
    return <Flashbar items={[{
        type: "success",
        content: DOWNLOAD_FILE_STATUS_MESSAGES.IN_PROGRESS_MESSAGE,
        loading: true,
    }]}/>;
}

export function DownloadError(content: string) {
    return <Flashbar items={[{
        type: "error",
        content: content,
        dismissible: true
    }]}/>
}

export function ActivityError(activity: string, header: string, content: string, onDismiss: any, errors?: string[]) {
    if (errors !== undefined && errors.length !== 0) {
        return <Flashbar items={[{
            type: "error",
            header: header,
            content: <Box variant="p">The following errors occurred during {activity}:<Box variant="p">{OrderedListStructure(errors)}</Box></Box>,
            onDismiss: onDismiss,
            dismissible: true
        }]}/>;
    } else {
        return <Flashbar items={[{
            type: "error",
            header: header,
            content: content,
            onDismiss: onDismiss,
            dismissible: true
        }]}/>;
    }
}

export async function GetAPI(query: any, queryOptions : any) {
    return await useQuery(query, queryOptions);
}

export function renderDateComponent(epochMilli: string, format: string, equipmentDataDropDownResult?: EquipmentDataDropDownResult, equipmentId?: string) {
    if (equipmentDataDropDownResult?.loading) {
        return (<Spinner/>);
    } 
    const timezoneName = getTimezone(equipmentId, equipmentDataDropDownResult?.data);
    return (<ZonedDateTime
                timeInEpochMilli={epochMilli}
                timezoneIANA={timezoneName}
                dateTimeFormat={format}
            ></ZonedDateTime>);
}

export function renderDateRangeComponent(startEpochMilli?: string, endEpochMilli?: string, equipmentDataDropDownResult?: EquipmentDataDropDownResult, equipmentId?: string) {
    if (equipmentDataDropDownResult?.loading) {
        return (<Spinner/>);
    } 
    const timezoneName = getTimezone(equipmentId, equipmentDataDropDownResult?.data);
    return (<ZonedDateTimeRange
                startTimeInEpochMilli={startEpochMilli}
                endTimeInEpochMilli={endEpochMilli}
                timezoneIANA={timezoneName}
            ></ZonedDateTimeRange>);
}

export function renderMeasurementComponent(value?: number, unitOfMeasure?: string) {
    return (<Measurement
                value = {value}
                unitOfMeasure = {unitOfMeasure}
            ></Measurement>);
}

const VOLUME_TO_M3_CONVERSIONS: Record<string, number> = {
    CM: 1,
    CF: 0.0283168,
};

export function renderVolumeInM3Component(value?: number, unitOfMeasure?: string) {
    if (value === undefined || unitOfMeasure === undefined) return renderMeasurementComponent(undefined, undefined);
    const factor = VOLUME_TO_M3_CONVERSIONS[unitOfMeasure];
    return renderMeasurementComponent(factor ? parseFloat((value * factor).toFixed(4)) : value, factor ? "m³" : unitOfMeasure);
}

export function renderWeightComponent(value?: number, unitOfMeasure?: string) {
    if (value === undefined || unitOfMeasure === undefined) return renderMeasurementComponent(undefined, undefined);
    return renderMeasurementComponent(parseFloat(value.toFixed(2)), unitOfMeasure);
}

export function renderFileError(errorMsg: any) {
    return (<Box>
                <SpaceBetween direction="horizontal" size={"s"}>
                    <Icon variant="warning" name="status-warning"></Icon>
                    <Box color="text-status-error" variant="small"> {errorMsg} </Box>
                </SpaceBetween>
            </Box>);
}

export function renderFileInfo(fileName: string, fileSize: any) {
    return (<>
            <Box>
                <SpaceBetween direction="horizontal" size={"xs"}>
                    <Icon  variant="success" name="status-positive" />
                    <Box> {fileName} </Box>
                </SpaceBetween>
            </Box>
            {fileSize ? 
            <Box color="text-body-secondary" variant="small" padding={{"left": "xl"}}>
                {fileSize}
            </Box> : null}
            </>);
}

export function getForbiddenMessageWithTemplateLink(message: string, isFlashBar: boolean) {
    return <>{message} To get access, open a ticket via this <Link color={isFlashBar ? 'inverted' : 'normal'} external={true} href = {SHIPMENT_REQUEST_ACCESS_TICKET_LINK}>link</Link></>
}

export function getForbiddenMessage(message: React.ReactNode, isFlashBar: boolean) {
    var messageToBeDisplayed = message && message !== ACCESS_DENIED_MESSAGE ? message : null;
    if (credentialStorage.isExternalUser()) {
        return <>{messageToBeDisplayed} To get access, contact AWS support team : <Link color={isFlashBar ? 'inverted' : 'normal'} external={true} href="mailto:aws-scm-shipment-support@amazon.com">aws-scm-shipment-support@amazon.com</Link></>
    }
    return <div> {messageToBeDisplayed} To get access, open a ticket via this <Link color={isFlashBar ? 'inverted' : 'normal'} external={true} href = {SHIPMENT_REQUEST_ACCESS_TICKET_LINK}>link</Link></div>
}